import * as React from 'react';

import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { Typography } from '@mui/material';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import OutlinedInput from '@mui/material/OutlinedInput';

import { black, gray, primarycolor } from '../../constants/Colors';
import { formLabelWrapper, textMb } from '../../constants/Location';

import type { SxProps } from '@mui/material';
import type { FieldError, FieldErrorsImpl, Merge } from 'react-hook-form';

interface State {
  amount: string;
  password: string;
  weight: string;
  weightRange: string;
  showPassword: boolean;
}

type OutlinedInputProps = React.ComponentProps<typeof OutlinedInput>;

type Props = OutlinedInputProps & {
  labelText?: string;
  requiredText?: string;
  isRequired: boolean;
  error?: boolean;
  helperText?:
    | string
    | FieldError
    | Merge<FieldError, FieldErrorsImpl<any>>
    | undefined;
  width?: string;
};

const styles = {
  formLabelWrapper,
  helper: {
    '@media screen and (max-width:480px)': {
      marginLeft: 0
    }
  },
  label: {
    fontSize: '12px'
  },
  labelFont: {
    '@media screen and (max-width:480px)': {
      marginLeft: 0
    },
    fontSize: '12px',
    marginLeft: '24px'
  },
  required: {
    color: primarycolor,
    fontSize: '12px',
    marginLeft: '10px'
  }
};

const PasswordTextFiled = React.forwardRef(
  (props: Props): React.ReactElement => {
    const { labelText, requiredText, isRequired, error, helperText, ...rest } =
      props;

    const [values, setValues] = React.useState<State>({
      amount: '',
      password: '',
      showPassword: false,
      weight: '',
      weightRange: ''
    });

    const handleClickShowPassword = (): void => {
      setValues({
        ...values,
        showPassword: !values.showPassword
      });
    };

    const handleMouseDownPassword = (
      event: React.MouseEvent<HTMLButtonElement>
    ): void => {
      event.preventDefault();
    };

    function inputBorderColor(): SxProps {
      if (error) {
        return {
          '&.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
            borderColor: primarycolor
          },
          '@media screen and (max-width:480px)': {
            width: '100%'
          },
          height: '40px',
          width: '400px'
        };
      }
      return {
        '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
          {
            borderColor: black
          },
        '@media screen and (max-width:480px)': {
          width: '100%'
        },
        height: '40px',
        width: '400px'
      };
    }

    const textfieldHelper = (): string | undefined => {
      if (helperText === undefined) return;
      return String(helperText);
    };

    return (
      <Typography sx={textMb} component="div">
        <Typography align="left" sx={styles.formLabelWrapper} component="div">
          <Typography color={gray} sx={styles.label} component="span">
            {labelText}
          </Typography>
          {isRequired ? (
            <Typography component="span" sx={styles.required}>
              {requiredText}
            </Typography>
          ) : (
            ''
          )}
        </Typography>
        <OutlinedInput
          {...rest}
          type={values.showPassword ? 'text' : 'password'}
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={handleClickShowPassword}
                onMouseDown={handleMouseDownPassword}
                edge="end"
              >
                {values.showPassword ? <Visibility /> : <VisibilityOff />}
              </IconButton>
            </InputAdornment>
          }
          sx={inputBorderColor()}
        />
        <FormHelperText sx={styles.helper} error={error}>
          {textfieldHelper()}
        </FormHelperText>
      </Typography>
    );
  }
);
PasswordTextFiled.displayName = 'PasswordTextFiled';
export default PasswordTextFiled;
